'use client';

import {useState} from 'react';
import {twJoin} from 'tailwind-merge';
import RatePlaceButtonWrapper from '../rate-place/RatePlaceButtonWrapper';
import getImageAltText from 'src/utils/getImageAltText';
import formatPlaceType from 'src/utils/formatPlaceType';
import {checkLine, starFill} from 'src/utils/icons';
import ResponsiveModal from '../ResponsiveModal';
import getImageUrl from 'src/utils/getImageUrl';
import useMounted from 'src/hooks/useMounted';
import FadingImage from '../FadingImage';
import BaseButton from '../BaseButton';

type Props = {
  initialPlaces: Place[];
  session: IronSession;
  myReviews: Review[];
};

const VisitedMapEmptyState = ({initialPlaces, session, myReviews}: Props) => {
  const shouldShowEmptyState = myReviews.length === 0;

  const [isOpen, setIsOpen] = useState(shouldShowEmptyState);
  const [places] = useState(initialPlaces);

  const mounted = useMounted();

  return (
    <div
      className={twJoin(
        'bg-[rgba(255,255,255,0.6)] dark:bg-[rgba(22,15,25,0.6)] absolute left-0 -top-4 right-0 bottom-0 z-5 backdrop-blur-sm transition-opacity duration-1000',
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}
    >
      {mounted ? (
        <ResponsiveModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          closeOnEscape={false}
          isDismissable={false}
          title="Rate your first places"
          className="max-h-[600px]"
        >
          <div className="mt-2 mb-5">
            <p className="text-balance">Start bulding your map by rating places you have visited.</p>
          </div>

          {places.map((place) => {
            const firstImage = place.images?.[0];
            const imageUrl = firstImage ? getImageUrl(firstImage.path, 'thumb') : '';
            const placeReview = myReviews.find((review) => review.place === place._id);

            return (
              <div key={place._id} className="group h-[64px] w-full flex items-center justify-between">
                <div className="h-12 w-12 mr-4 shrink-0 bg-(--secondary-color) nice-image rounded-xl after:rounded-xl">
                  {firstImage ? (
                    <FadingImage
                      src={imageUrl}
                      fill
                      className="object-cover"
                      alt={getImageAltText(firstImage, place.name, place.countryName)}
                    />
                  ) : null}
                </div>

                <div className="flex flex-col grow items-start w-0">
                  <div className="flex items-center gap-[2px] w-full">
                    <span className="text-lg font-semibold truncate leading-snug">{place.name}</span>
                  </div>
                  <span className="text-sm text-(--gray-text) text-left truncate w-full">
                    {formatPlaceType(place.placeType)} in {place.countryName}
                  </span>
                </div>

                <RatePlaceButtonWrapper place={place} initialReview={placeReview}>
                  <BaseButton
                    disabled={!session}
                    label={placeReview ? 'Rated' : 'Rate'}
                    icon={placeReview ? checkLine : starFill}
                    iconSize={16}
                    size="small"
                    buttonVariant={placeReview || !session ? 'secondary' : 'primary'}
                    className="text-base"
                  />
                </RatePlaceButtonWrapper>
              </div>
            );
          })}

          <BaseButton
            buttonVariant="secondary"
            icon={checkLine}
            label="I'm done!"
            className="w-full mt-5"
            onPress={() => setIsOpen(false)}
          />
        </ResponsiveModal>
      ) : null}
    </div>
  );
};

export default VisitedMapEmptyState;
